import React from "react";
import { useState, useCallback, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { useOnboardingSettings, AdAccountSettings } from "@/hooks/useOnboardingSettings";
import { useUnconfiguredAccounts, UnconfiguredAdAccount } from "@/hooks/useUnconfiguredAccounts";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingStepper } from "@/components/onboarding/OnboardingStepper";
import { AccountSelectionStep } from "@/components/onboarding/steps/AccountSelectionStep";
import { AdProfilesStep } from "@/components/onboarding/steps/AdProfilesStep";
import { TrackingStep } from "@/components/onboarding/steps/TrackingStep";
import { NamingConventionStep } from "@/components/onboarding/steps/NamingConventionStep";
import { LaunchSettingsStep } from "@/components/onboarding/steps/LaunchSettingsStep";
import { CreativeEnhancementsStep } from "@/components/onboarding/steps/CreativeEnhancementsStep";
import { ArrowLeft, ArrowRight, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STEP_TITLES = [
  "Ad Profiles",
  "Tracking",
  "Naming Convention",
  "Launch Settings",
  "Creative Enhancements",
];

export default function OnboardingSetup() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { adAccounts: contextAdAccounts, setSelectedAdAccount } = useAdAccount();
  const { triggerBackgroundSync } = useBackgroundSync();
  const queryClient = useQueryClient();

  // Fetch unconfigured accounts
  const { data: unconfiguredAccounts, isLoading: accountsLoading } = useUnconfiguredAccounts();

  // Selected account for the wizard
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(null);

  // Step 0 = account selection (only shown when 2+ unconfigured)
  // Steps 1-5 = wizard steps
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [showSuccess, setShowSuccess] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Onboarding settings for the selected account
  const { settings, isLoading: settingsLoading, saveStep, completeOnboarding, isSaving } =
    useOnboardingSettings(selectedAccountId ?? undefined);

  // Local form state
  const [formData, setFormData] = useState<Partial<AdAccountSettings>>({});

  // Seed form data from loaded settings
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  // Auto-sync on mount when account is selected
  useEffect(() => {
    if (user?.id && selectedAccountId) {
      triggerBackgroundSync(user.id, selectedAccountId);
    }
  }, [user?.id, selectedAccountId]);

  // Initialize: decide starting step based on unconfigured account count
  useEffect(() => {
    if (initialized || !unconfiguredAccounts) return;

    if (unconfiguredAccounts.length === 0) {
      // All accounts configured — redirect away
      navigate("/creatives/library", { replace: true });
      return;
    }

    if (unconfiguredAccounts.length === 1) {
      // Auto-select the single account, skip to step 1
      const account = unconfiguredAccounts[0];
      setSelectedAccountId(account.ad_account_id);
      setSelectedAccountName(account.ad_account_name);
      setCurrentStep(1);
      markAccountInProgress(account.ad_account_id);
    } else {
      // 2+ accounts — show step 0 (account selection)
      setCurrentStep(0);
    }

    setInitialized(true);
  }, [unconfiguredAccounts, initialized]);

  const markAccountInProgress = async (adAccountId: string) => {
    try {
      await supabase
        .from("ad_accounts")
        .update({ setup_status: "in_progress" as any })
        .eq("user_id", user!.id)
        .eq("ad_account_id", adAccountId);

      // Invalidate cached status
      queryClient.invalidateQueries({ queryKey: ["ad-account-row"] });
      queryClient.invalidateQueries({ queryKey: ["unconfigured-accounts"] });
    } catch (err) {
      console.error("Failed to update setup_status:", err);
    }
  };

  const markAccountCompleted = async (adAccountId: string) => {
    try {
      await supabase
        .from("ad_accounts")
        .update({ setup_status: "completed" as any })
        .eq("user_id", user!.id)
        .eq("ad_account_id", adAccountId);

        queryClient.invalidateQueries({ queryKey: ["ad-account-row"] });
        queryClient.invalidateQueries({ queryKey: ["unconfigured-accounts"] });
        queryClient.invalidateQueries({ queryKey: ["ad-account-list"] });
        queryClient.invalidateQueries({ queryKey: ["ad-account-statuses"] });
    } catch (err) {
      console.error("Failed to update setup_status:", err);
    }
  };

  // Resolve display name for header
  const accountName =
    selectedAccountName ??
    contextAdAccounts.find((a) => a.id === selectedAccountId)?.name ??
    selectedAccountId ??
    "";

  const canContinue = useCallback(() => {
    if (currentStep === 0) return !!selectedAccountId;
    if (currentStep === 1) return !!formData.default_fb_page_id;
    return true;
  }, [currentStep, selectedAccountId, formData.default_fb_page_id]);

  const handleContinue = async () => {
    // Step 0 → proceed to step 1
    if (currentStep === 0) {
      if (!selectedAccountId) return;
      await markAccountInProgress(selectedAccountId);
      setCurrentStep(1);
      return;
    }

    try {
      await saveStep(formData);
      setCompletedSteps((prev) => new Set([...prev, currentStep]));

      if (currentStep === 5) {
        await completeOnboarding();
        if (selectedAccountId) {
          await markAccountCompleted(selectedAccountId);
          // Set as active account in context
          setSelectedAdAccount(selectedAccountId, selectedAccountName);
        }
        setShowSuccess(true);
        setTimeout(() => navigate("/creatives/library"), 1500);
      } else {
        setCurrentStep((s) => s + 1);
      }
    } catch (err) {
      toast.error("Failed to save. Please try again.");
      console.error(err);
    }
  };

  const handleBack = () => {
    if (currentStep === 1 && unconfiguredAccounts && unconfiguredAccounts.length > 1) {
      setCurrentStep(0);
    } else if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleStepClick = (step: number) => {
    if (completedSteps.has(step)) setCurrentStep(step);
  };

  const handleAccountSelect = (account: UnconfiguredAdAccount) => {
    setSelectedAccountId(account.ad_account_id);
    setSelectedAccountName(account.ad_account_name);
  };

  const updateFormData = (partial: Partial<AdAccountSettings>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  // Auth guard
  if (!authLoading && !user) return <Navigate to="/auth" replace />;

  if (authLoading || accountsLoading || !initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-pulse text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background gap-3">
        <CheckCircle2 className="h-12 w-12 text-green-500" />
        <h2 className="text-xl font-semibold text-foreground">Setup complete!</h2>
        <p className="text-sm text-muted-foreground">Redirecting you now...</p>
      </div>
    );
  }

  const isStep0 = currentStep === 0;
  const showBackButton = currentStep > 1 || (currentStep === 1 && unconfiguredAccounts && unconfiguredAccounts.length > 1);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">
          {isStep0 ? "Account Setup" : `Set up ${accountName}`}
        </h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/launcher/ad-sheets")}
          className="text-muted-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          Skip for now
        </Button>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {isStep0 ? (
          /* Step 0: Account selection — no stepper */
          <div className="max-w-[640px] mx-auto">
            <AccountSelectionStep
              accounts={unconfiguredAccounts ?? []}
              selectedAccountId={selectedAccountId}
              onSelect={handleAccountSelect}
            />
            <div className="flex justify-end mt-6">
              <Button onClick={handleContinue} disabled={!canContinue()}>
                Continue
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        ) : (
          /* Steps 1-5: Wizard with stepper */
          <>
            {/* Mobile stepper */}
            <div className="md:hidden">
              <OnboardingStepper
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
              />
            </div>

            <div className="flex gap-8">
              {/* Desktop stepper */}
              <div className="hidden md:block">
                <OnboardingStepper
                  currentStep={currentStep}
                  completedSteps={completedSteps}
                  onStepClick={handleStepClick}
                />
              </div>

              {/* Step content */}
              <div className="flex-1 max-w-[640px]">
                {currentStep === 1 && selectedAccountId && (
                  <AdProfilesStep
                    adAccountId={selectedAccountId}
                    fbPageId={formData.default_fb_page_id ?? null}
                    fbPageName={formData.default_fb_page_name ?? null}
                    igAccountId={formData.default_ig_account_id ?? null}
                    igAccountName={formData.default_ig_account_name ?? null}
                    onChange={updateFormData}
                  />
                )}

                {currentStep === 2 && (
                  <TrackingStep
                    data={{
                      default_pixel_id: formData.default_pixel_id ?? null,
                      default_pixel_name: formData.default_pixel_name ?? null,
                      default_app_id: formData.default_app_id ?? null,
                      default_app_name: formData.default_app_name ?? null,
                      website_events_enabled: formData.website_events_enabled ?? false,
                      app_events_enabled: formData.app_events_enabled ?? false,
                      offline_events_enabled: formData.offline_events_enabled ?? false,
                      crm_events_enabled: formData.crm_events_enabled ?? false,
                      post_engagements_enabled: formData.post_engagements_enabled ?? false,
                      page_engagements_enabled: formData.page_engagements_enabled ?? false,
                      default_utm_parameters: (formData as any).default_utm_parameters ?? "",
                    }}
                    onChange={updateFormData}
                    adAccountId={selectedAccountId ?? undefined}
                  />
                )}

                {currentStep === 3 && (
                  <NamingConventionStep
                    data={{
                      naming_convention: (formData.naming_convention as { tags: string[]; separator: string } | null) ?? null,
                    }}
                    onChange={updateFormData}
                  />
                )}
                {currentStep === 4 && (
                  <LaunchSettingsStep
                    data={{
                      multi_advertiser_opt_in: formData.multi_advertiser_opt_in ?? true,
                      default_ad_status: formData.default_ad_status ?? "PAUSED",
                    }}
                    onChange={updateFormData}
                  />
                )}
                {currentStep === 5 && (
                  <CreativeEnhancementsStep
                    data={{
                      image_enhancements: (formData.image_enhancements as Record<string, boolean> | null) ?? null,
                      video_enhancements: (formData.video_enhancements as Record<string, boolean> | null) ?? null,
                    }}
                    onChange={updateFormData}
                  />
                )}

                {/* Navigation buttons */}
                <div className="flex items-center justify-between mt-6">
                  {showBackButton ? (
                    <Button variant="outline" onClick={handleBack} disabled={isSaving}>
                      <ArrowLeft className="h-4 w-4 mr-1.5" />
                      Back
                    </Button>
                  ) : (
                    <div />
                  )}
                  <Button
                    onClick={handleContinue}
                    disabled={!canContinue() || isSaving}
                  >
                    {isSaving
                      ? "Saving..."
                      : currentStep === 5
                      ? "Finish Setup"
                      : "Continue"}
                    {!isSaving && currentStep < 5 && (
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
